"use client";

import { useTheme } from "@/contexts/theme-context";
import { Toaster as Sonner } from "sonner";
import { cn } from "@/lib/utils";
import { useIsDesktop } from "@/hooks/use-mobile";

type ToasterProps = React.ComponentProps<typeof Sonner>;

const Toaster = ({ ...props }: ToasterProps) => {
  const { actualTheme } = useTheme();
  const isDesktop = useIsDesktop();
  const isMobile = !isDesktop;

  return (
    <Sonner
      theme={actualTheme as ToasterProps["theme"]}
      className={cn("toaster group", isMobile && "toaster-mobile")}
      position={isDesktop ? "top-center" : "bottom-left"}
      offset={isDesktop ? 16 : undefined}
      gap={isMobile ? 6 : 8}
      visibleToasts={isMobile ? 3 : 4}
      toastOptions={{
        classNames: {
          toast: cn(
            "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg",
            isMobile &&
              "min-h-0 gap-1.5 rounded-md px-2.5 py-2 shadow-md w-[min(calc(100vw-1.5rem),280px)] max-w-[280px]",
          ),
          title: cn(isMobile && "text-xs font-medium leading-tight"),
          description: cn(
            "group-[.toast]:text-muted-foreground",
            isMobile && "text-[11px] leading-snug",
          ),
          actionButton: cn(
            "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
            isMobile && "h-6 px-2 text-[11px]",
          ),
          cancelButton: cn(
            "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
            isMobile && "h-6 px-2 text-[11px]",
          ),
          closeButton: cn(
            isMobile &&
              "size-5 border-0 bg-transparent text-muted-foreground [&_svg]:size-3",
          ),
          icon: cn(isMobile && "[&_svg]:size-3.5"),
        },
      }}
      {...props}
    />
  );
};

export { Toaster };
